/**
 * Email obfuscation utility for privacy protection in logging
 *
 * Examples:
 * - "john.doe@example.com" → "jo****@example.com"
 * - "a@test.com" → "a*****@test.com"
 * - "manager@kroger.com" → "ma******@kroger.com"
 * - "invalid-email" → "in**********" (no @ symbol)
 */

/**
 * Obfuscates an email address for privacy-safe logging
 * Shows first 2 characters of local part + full domain, masks the rest
 */
export const obfuscateEmail = (email: string | null): string | null => {
  if (email === null) {
    return null
  }

  if (!email || typeof email !== 'string') {
    return null
  }

  const atIndex = email.indexOf('@')

  if (atIndex === -1) {
    // No @ symbol found - probably not an email, don't obfuscate
    return email
  }

  const localPart = email.slice(0, atIndex)
  const domain = email.slice(atIndex) // Includes the @

  if (localPart.length === 0) {
    return '*****' + domain // Empty local part edge case
  }

  if (localPart.length === 1) {
    return localPart + '*' + domain // Single character local part
  }

  if (localPart.length === 2) {
    return localPart + domain // Show both characters if only 2
  }

  // Show first 2 characters, mask the exact number of remaining characters
  const visiblePart = localPart.slice(0, 2)
  const hiddenCharCount = localPart.length - 2
  const maskedPart = '*'.repeat(hiddenCharCount)

  return visiblePart + maskedPart + domain
}

/**
 * Obfuscates multiple emails in a string or array
 */
export const obfuscateEmailsInText = (text: string): string => {
  // Simple email regex - matches most common email formats
  const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g

  // @ts-expect-error TS2769 — strictNullChecks. TODO: fix strict null check
  return text.replace(emailRegex, match => obfuscateEmail(match))
}

/**
 * Creates a privacy-safe user context with obfuscated email
 */
export const createPrivacySafeUserContext = <
  T extends { email: string | null; emailError?: string },
>(
  userContext: T
): T & { originalEmailHash: string } => ({
  ...userContext,
  email: obfuscateEmail(userContext.email) as T['email'],
  originalEmailHash: hashEmail(userContext.email), // For correlation without exposing PII
})

/**
 * Simple hash function for email correlation
 * Used to correlate logs without exposing the actual email
 */
const hashEmail = (email: string | null): string => {
  if (email === null || !email) {
    return 'no_hash'
  }

  let hash = 0
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }

  return Math.abs(hash).toString(16).slice(0, 8) // 8 character hex
}
